import type { ApiResponse } from "@/types/auth";

export const TIME_SYNC_ERROR_EVENT = "b03:time-sync-error";
export const TIME_SYNC_RECHECK_HEADER = "X-Time-Sync-Recheck";

type TimeSyncResponse = Partial<ApiResponse<unknown>> & {
  server_time?: number | string | null;
};

export type TimeSyncSnapshot = {
  deviceTime: number;
  serverTime?: number | null;
};

export class TimeSyncError extends Error {
  snapshot: TimeSyncSnapshot;

  constructor(snapshot: TimeSyncSnapshot) {
    super("Client time differs from server time");
    this.name = "TimeSyncError";
    this.snapshot = snapshot;
  }
}

export function isTimeSyncError(error: unknown) {
  return error instanceof TimeSyncError || (error instanceof Error && error.name === "TimeSyncError");
}

function normalizeServerTime(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed < 10_000_000_000 ? parsed * 1000 : parsed;
}

export function createTimeSyncSnapshot(responseData?: TimeSyncResponse): TimeSyncSnapshot {
  return {
    deviceTime: Date.now(),
    serverTime: normalizeServerTime(responseData?.server_time),
  };
}

export function isTimeSyncAuthError(responseData: unknown) {
  if (!responseData || typeof responseData !== "object") return false;

  const data = responseData as TimeSyncResponse;
  return Number(data.code) === 401 && String(data.msg ?? "").trim().toLowerCase() === "token expired";
}

export function dispatchTimeSyncError(snapshot: TimeSyncSnapshot) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<TimeSyncSnapshot>(TIME_SYNC_ERROR_EVENT, { detail: snapshot }));
}

export function emitTimeSyncError(responseData?: TimeSyncResponse) {
  const snapshot = createTimeSyncSnapshot(responseData);
  dispatchTimeSyncError(snapshot);
  return new TimeSyncError(snapshot);
}
